import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, ShieldCheck, CheckCircle2, XCircle, RefreshCw, Loader2 } from "lucide-react";

const fmtBytes = (b) => {
  if (!b || b <= 0) return "";
  const mb = b / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
};

const fmtEta = (secs, fr) => {
  if (secs == null || !isFinite(secs) || secs < 0) return "";
  const s = Math.round(secs);
  if (s < 1) return fr ? "quelques secondes" : "a few seconds";
  if (s < 60) return fr ? `~${s} s restantes` : `~${s}s left`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return fr ? `~${m} min ${r ? r + " s" : ""} restantes` : `~${m}m ${r ? r + "s" : ""} left`;
};

/**
 * One-click APK download with a golden progress bar + best-effort auto-install.
 * A browser cannot force-open the Android installer, so after download we save
 * the file and immediately trigger opening it (surfaces the system Install
 * prompt on most Android browsers) with a reliable "Tap to Install" fallback.
 */
export default function ApkInstaller({ apkUrl, filename = "RoyalChat.apk", version, size = 0, fr = false, autoStart = false }) {
  const [phase, setPhase] = useState("idle"); // idle | downloading | ready | error
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(size || 0);
  const [speed, setSpeed] = useState(0);

  const abortRef = useRef(null);
  const blobUrlRef = useRef(null);
  const startRef = useRef(0);

  const revoke = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
    revoke();
  }, [revoke]);

  const triggerInstall = useCallback((url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [filename]);

  // Fire a "Download complete" system notification; tapping it re-opens the APK.
  const notifyComplete = useCallback(async () => {
    try {
      if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
      let perm = Notification.permission;
      if (perm === "default") {
        try { perm = await Notification.requestPermission(); } catch (_) { /* ignore */ }
      }
      if (perm !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(fr ? "Téléchargement terminé" : "Download complete", {
        body: fr ? "Touchez pour ouvrir et installer RoyalChat Nova." : "Tap to open and install RoyalChat Nova.",
        tag: "rc-apk-download",
        renotify: true,
        requireInteraction: true,
        icon: "/icons/icon-192.png",
        badge: "/icons/icon-192.png",
        data: { type: "apk-open" },
      });
    } catch (_) { /* notifications are best-effort */ }
  }, [fr]);

  // When the user taps the notification, the SW asks this page to open the file.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMsg = (ev) => {
      if (ev.data && ev.data.type === "rc-open-apk") {
        triggerInstall(blobUrlRef.current || apkUrl);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [triggerInstall, apkUrl]);

  // Native browser download (streams straight to disk, no memory cap, resumable via Range).
  const directDownload = useCallback(() => {
    triggerInstall(apkUrl);
  }, [apkUrl, triggerInstall]);

  const start = useCallback(async () => {
    revoke();
    // Large APKs (e.g. 150MB) can blow past browser memory when buffered as a Blob for the
    // progress bar — hand those to the browser's native downloader which streams to disk.
    const LARGE = 60 * 1024 * 1024;
    if (size && size > LARGE) {
      setPhase("ready");
      directDownload();
      return;
    }
    setPhase("downloading");
    setLoaded(0);
    setSpeed(0);
    setTotal(size || 0);
    const controller = new AbortController();
    abortRef.current = controller;
    startRef.current = Date.now();
    try {
      const res = await fetch(apkUrl, { signal: controller.signal, cache: "no-store" });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const len = Number(res.headers.get("Content-Length")) || size || 0;
      setTotal(len);
      const reader = res.body.getReader();
      const chunks = [];
      let received = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        setLoaded(received);
        const elapsed = (Date.now() - startRef.current) / 1000;
        if (elapsed > 0.3) setSpeed(received / elapsed);
      }
      const blob = new Blob(chunks, { type: "application/vnd.android.package-archive" });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setPhase("ready");
      triggerInstall(url); // auto-open / save immediately
      notifyComplete();    // "Download complete" system notification
    } catch (e) {
      if (controller.signal.aborted) {
        setPhase("idle");
        return;
      }
      // Memory/network failure buffering the file — fall back to a native browser download.
      setPhase("ready");
      directDownload();
    }
  }, [apkUrl, size, revoke, triggerInstall, directDownload, notifyComplete]);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setPhase("idle");
    setLoaded(0);
    setSpeed(0);
  }, []);

  // Auto-start the download shortly after mount (hero "Download" flow). The small
  // delay lets React StrictMode's dev remount settle so the fetch isn't aborted.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStart) return undefined;
    const id = setTimeout(() => {
      if (!autoStartedRef.current) { autoStartedRef.current = true; start(); }
    }, 250);
    return () => clearTimeout(id);
  }, [autoStart, start]);

  const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
  const etaSecs = speed > 0 && total > 0 ? (total - loaded) / speed : null;

  const btnBase =
    "w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold py-4 text-lg transition-colors";

  return (
    <div data-testid="apk-installer" className="mt-5">
      <AnimatePresence mode="wait">
        {/* ---------------- IDLE ---------------- */}
        {phase === "idle" && (
          <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button
              onClick={start}
              data-testid="apk-install-start-btn"
              className={`${btnBase} bg-[var(--rc-gold)] text-black hover:bg-[var(--rc-gold-light)] shadow-[0_8px_30px_-8px_var(--rc-gold)]`}
            >
              <Download size={18} /> {fr ? "Télécharger RoyalChat Nova" : "Download RoyalChat Nova"}
            </button>
            <div className="mt-3 flex items-center justify-center gap-4 text-sm text-[var(--rc-muted)]" data-testid="apk-meta">
              {version && (
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck size={14} className="text-[var(--rc-gold)]" /> v{version}
                </span>
              )}
              {fmtBytes(size) && <span>{fmtBytes(size)}</span>}
            </div>
          </motion.div>
        )}

        {/* ---------------- DOWNLOADING ---------------- */}
        {phase === "downloading" && (
          <motion.div key="dl" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            data-testid="apk-downloading" className="rounded-2xl border border-[var(--rc-gold)]/25 bg-[var(--rc-bg)]/50 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--rc-text)]">
                <Loader2 size={16} className="animate-spin text-[var(--rc-gold)]" />
                {fr ? "Téléchargement de RoyalChat Nova…" : "Downloading RoyalChat Nova…"}
              </span>
              <span data-testid="apk-progress-percent" className="text-lg font-bold text-[var(--rc-gold)] tabular-nums">
                {total > 0 ? `${percent}%` : fmtBytes(loaded)}
              </span>
            </div>

            {/* Golden progress bar */}
            <div className="h-3 w-full overflow-hidden rounded-full bg-[var(--rc-surface)] border border-[var(--rc-gold)]/15" data-testid="apk-progress-bar">
              <motion.div
                className="relative h-full rounded-full"
                style={{
                  width: total > 0 ? `${percent}%` : "40%",
                  background: "linear-gradient(90deg, var(--rc-gold-light, #f0d879), var(--rc-gold), var(--rc-gold-light, #f0d879))",
                  boxShadow: "0 0 12px 0 var(--rc-gold)",
                }}
                animate={total > 0 ? {} : { x: ["-40%", "160%"] }}
                transition={total > 0 ? {} : { repeat: Infinity, duration: 1.1, ease: "easeInOut" }}
              >
                <span className="absolute inset-0 rounded-full opacity-40"
                  style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)" }} />
              </motion.div>
            </div>

            <div className="mt-3 flex items-center justify-between text-xs text-[var(--rc-muted)] tabular-nums">
              <span data-testid="apk-progress-size">
                {fmtBytes(loaded)}{total > 0 ? ` / ${fmtBytes(total)}` : ""}
              </span>
              <span>{fmtEta(etaSecs, fr)}</span>
            </div>

            <button onClick={cancel} data-testid="apk-cancel-btn"
              className="mt-4 w-full rounded-full border border-[var(--rc-border)] py-2.5 text-sm text-[var(--rc-muted)] hover:text-[var(--rc-text)] hover:border-[var(--rc-gold)]/40 transition-colors">
              {fr ? "Annuler" : "Cancel"}
            </button>
          </motion.div>
        )}

        {/* ---------------- READY / SUCCESS — where to find & install the APK ---------------- */}
        {phase === "ready" && (
          <motion.div key="ready" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            data-testid="apk-install-success" className="rounded-2xl border border-[var(--rc-gold)]/30 bg-[var(--rc-bg)]/50 p-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0 grid h-9 w-9 place-items-center rounded-full bg-[var(--rc-gold)]/15 text-[var(--rc-gold)]">
                <CheckCircle2 size={20} />
              </span>
              <div>
                <p className="font-semibold text-[var(--rc-text)]">
                  {fr ? "Comment installer RoyalChat Nova" : "How to install RoyalChat Nova"}
                </p>
                <p className="mt-1 text-sm text-[var(--rc-muted)] leading-relaxed">
                  {fr
                    ? "Suivez ces étapes pour trouver et installer l’application :"
                    : "Follow these steps to find and install the app:"}
                </p>
              </div>
            </div>

            <ol data-testid="apk-install-instructions" className="mt-4 space-y-3">
              {(fr
                ? [
                    "Ouvrez vos Téléchargements — faites glisser la barre de notifications vers le bas, ou ouvrez l’application Fichiers / Téléchargements.",
                    "Touchez le fichier RoyalChat Nova que vous venez de télécharger.",
                    "Si votre téléphone le demande, autorisez « Installer depuis cette source » (applications inconnues).",
                    "Touchez « Installer », puis ouvrez RoyalChat Nova.",
                  ]
                : [
                    "Open your Downloads — swipe down the notification shade, or open the Files / Downloads app.",
                    "Tap the RoyalChat Nova file you just downloaded.",
                    "If your phone asks, allow “Install from this source” (unknown apps).",
                    "Tap “Install”, then open RoyalChat Nova.",
                  ]
              ).map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0 grid h-6 w-6 place-items-center rounded-full bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] text-xs font-bold tabular-nums">
                    {i + 1}
                  </span>
                  <span className="text-sm text-[var(--rc-text)] leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>

            <button onClick={start} data-testid="apk-redownload-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[var(--rc-border)] py-2.5 text-sm text-[var(--rc-muted)] hover:text-[var(--rc-text)] hover:border-[var(--rc-gold)]/40 transition-colors">
              <RefreshCw size={14} /> {fr ? "Télécharger à nouveau" : "Download again"}
            </button>
          </motion.div>
        )}

        {/* ---------------- ERROR ---------------- */}
        {phase === "error" && (
          <motion.div key="err" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            data-testid="apk-install-error" className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0 grid h-9 w-9 place-items-center rounded-full bg-red-500/15 text-red-400">
                <XCircle size={20} />
              </span>
              <div>
                <p className="font-semibold text-[var(--rc-text)]">
                  {fr ? "Échec du téléchargement" : "Download failed"}
                </p>
                <p className="mt-1 text-sm text-[var(--rc-muted)] leading-relaxed">
                  {fr
                    ? "Vérifiez votre connexion et réessayez. Vous pouvez aussi ouvrir le lien directement."
                    : "Check your connection and try again. You can also open the link directly."}
                </p>
              </div>
            </div>

            <button onClick={start} data-testid="apk-retry-btn"
              className={`${btnBase} mt-4 bg-[var(--rc-gold)] text-black hover:bg-[var(--rc-gold-light)]`}>
              <RefreshCw size={18} /> {fr ? "Réessayer" : "Retry"}
            </button>
            <a href={apkUrl} download={filename} data-testid="apk-direct-link"
              className="mt-3 block text-center text-sm text-[var(--rc-gold)] hover:underline">
              {fr ? "Télécharger directement l'APK" : "Download the APK directly"}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
